import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { formatPhone } from '~/util';
import { ServicesContext } from '~/services';
import { ProviderType, getProviderInfo } from '~/util/providerUtils';
import { ProviderSpecialty } from '../ProviderResults';

interface SuggestEditModalProps {
    provider: ProviderType;
    setSuggestedEdit?: (x: boolean) => void;
}
export function SuggestEditModal({ provider, setSuggestedEdit }: SuggestEditModalProps) {
    const services = React.useContext(ServicesContext);
    const [show, setShow] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [suggestion, setSuggestion] = React.useState('');
    const { providerName, providerFax } = getProviderInfo(provider);
    const orgPracticeName = provider?.orgName || provider?.practiceName;

    async function submit() {
        setSubmitting(true);
        await services.api.call({
            method: 'post',
            url: '/core-api/referrals/suggest-edit',
            data: {
                suggestion,
                providerName,
                orgPracticeName,
                specialties: provider?.specialties?.map((spec) => spec.name).join(', '),
                npi: provider?.npi || '',
                fax: providerFax ? formatPhone(providerFax) : '',
                type: provider.entityType,
                id: provider.id,
            },
        });
        setSubmitted(true);
        setSubmitting(false);
        if (setSuggestedEdit) {
            setSuggestedEdit(true);
        }
    }

    function close() {
        setShow(false);
        setSuggestion('');
        setSubmitted(false);
    }

    return (
        <>
            <button className="link-button" type="button" onClick={() => setShow(true)}>
                Suggest an edit
            </button>
            <BSModal
                className="suggest-edit-modal"
                show={show}
                onHide={close}
                aria-labelledby="suggest-edit-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="suggest-edit-title" role="heading" aria-level={1}>
                        {submitted
                            ? 'Suggested Edit Received'
                            : 'Suggest Edit to Provider Information'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {submitted ? (
                        <div>
                            <p>
                                Your suggested edit has been received and is in review. Changes may
                                take some time to appear on the platform.
                            </p>
                            <p>We appreciate your help in improving our accuracy.</p>
                            <div className="text-center">
                                <i
                                    id="suggestion-success-icon"
                                    className="fa-regular fa-check-circle text-success"
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div>
                                <div className="fw-bold mb-2">Current provider information</div>
                                <div className="h5 text-ellipsis">{providerName}</div>
                                {orgPracticeName && (
                                    <div className="text-ellipsis" title={orgPracticeName}>
                                        {orgPracticeName}
                                    </div>
                                )}
                                {provider?.npi && (
                                    <div className="text-ellipsis" title={provider.npi}>
                                        {provider.npi}
                                    </div>
                                )}
                                <ProviderSpecialty specialties={provider?.specialties} />
                                {providerFax && (
                                    <div>
                                        Fax: {formatPhone(providerFax)}
                                        <i
                                            className="fa-regular fa-question-circle ms-2"
                                            title="Referrals to this provider will be faxed."
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="mt-3">
                                <label htmlFor="suggestion" className="fw-bold mb-1">
                                    Suggested provider information
                                </label>
                                <textarea
                                    id="suggestion"
                                    className="form-control"
                                    placeholder="Suggest an edit to this provider's information (e.g., practice name, fax number)"
                                    rows={4}
                                    value={suggestion}
                                    onChange={(e) => setSuggestion(e.target.value)}
                                    maxLength={2000}
                                />
                                <div className="fst-italic mt-1 text-end">
                                    Do not include PHI or other sensitive information.
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" type="button" onClick={close}>
                        {submitted ? 'Close' : 'Cancel'}
                    </button>
                    {!submitted && (
                        <button
                            type="submit"
                            disabled={submitting || suggestion.length === 0}
                            className="btn btn-primary"
                            onClick={submit}
                        >
                            {submitting && (
                                <Spinner
                                    animation="border"
                                    variant="light"
                                    size="sm"
                                    role="status"
                                    className="me-1"
                                />
                            )}
                            Submit
                        </button>
                    )}
                </Modal.Footer>
            </BSModal>
        </>
    );
}
